/**
 * About Your Business.
 * Feeds the /about page (About-screen) and the /team/:slug member pages (TeamMember).
 * `teamSection.members` is the source of truth for both, keep the member shape
 * (slug/name/role/description/bio/img/socials/seo) intact.
 *
 * `communitySection.images` is only a fallback collage; at runtime the page prefers
 * the live S3 gallery (SystemDataService.getGalleryImages). Add real photos
 * by uploading to the gallery / dropping URLs here.
 */

import { ASSETS } from "./assets-data";

// Seed collage, placeholder media. Live gallery overrides this when available.

const COMMUNITY_IMAGES = [
    '/placeholder.svg',
    '/placeholder.svg',
    '/placeholder.svg',
    '/placeholder.svg',
    '/placeholder.svg',
    '/placeholder.svg',
    '/placeholder.svg',
    '/placeholder.svg',
].filter(Boolean);

const AboutData = {
    en: {
        seo: {
            title: 'About {business} | Section Title',
            description:
                'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
            keywords:
                '{business}, keyword one, keyword two, keyword three',
            image: '/placeholder.svg',
        },

        heroSection: {
            title: 'Headline goes here',
            subtitle: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
            heroMedia: '/placeholder.svg',
            heroShorts: COMMUNITY_IMAGES,
        },

        aboutSection: {
            eyebrow: 'Who We Are',
            titlePre: 'About',
            titleAccent: 'Your Business',
            body:
                'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
        },

        whoWeAreSection: {
            eyebrow: 'Who We Are',
            headingLine1: 'Headline goes here',
            headingLine2: 'Section Title',
            description:
                'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
            ctaText: 'Learn More',
            ctaHref: '/about',
            image: ASSETS.MAESTROSHIELD_BUILDING_EXTERIOR_SIGN,
            imageAlt: 'Our team on site',
            decorImage: ASSETS.ROLLDOWN_SHUTTER,
            badgeValue: '25+',
            badgeLabel: 'Years\nof Experience',
            missions: [
                {
                    body:
                        'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
                    linkText: 'Our Mission',
                    linkHref: '/about',
                },
                {
                    body:
                        'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
                    linkText: 'Our Vision',
                    linkHref: '/about',
                },
                {
                    body:
                        'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
                    linkText: 'Our Values',
                    linkHref: '/about',
                },
            ],
        },

        teamSection: {
            eyebrow: 'Our Team',
            title: 'Section Title',
            description: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
            members: [
                {
                    slug: 'team-member-one',
                    name: 'Your Business Team',
                    role: 'Item One',
                    description:
                        'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
                    bio: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
                    img: '/placeholder.svg',
                    socials: [],
                    seo: {
                        title: '{business} Team | Section Title',
                        description:
                            'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
                        keywords: '{business}, keyword one, keyword two, keyword three',
                        image: '/placeholder.svg',
                    },
                },
            ],
        },

        communitySection: {
            eyebrow: 'Our Work',
            title: 'Section Title',
            subtitle: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
            images: COMMUNITY_IMAGES,
            ctaText: 'Services',
            ctaHref: '/services',
        },
    },

    he: {
        seo: {
            title: 'אודות {business} | Section Title',
            description:
                'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
            keywords:
                '{business}, keyword one, keyword two, keyword three',
            image: '/placeholder.svg',
        },

        heroSection: {
            title: 'Headline goes here',
            subtitle: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
            heroMedia: '/placeholder.svg',
            heroShorts: COMMUNITY_IMAGES,
        },

        aboutSection: {
            eyebrow: 'מי אנחנו',
            titlePre: 'אודות',
            titleAccent: 'Your Business',
            body:
                'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
        },

        whoWeAreSection: {
            eyebrow: 'מי אנחנו',
            headingLine1: 'Headline goes here',
            headingLine2: 'Section Title',
            description:
                'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
            ctaText: 'קראו עוד',
            ctaHref: '/about',
            image: '/placeholder.svg',
            imageAlt: 'הצוות שלנו בשטח',
            decorImage: '/placeholder.svg',
            badgeValue: '+25',
            badgeLabel: 'שנות\nניסיון',
            missions: [
                {
                    body:
                        'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
                    linkText: 'המשימה שלנו',
                    linkHref: '/about',
                },
                {
                    body:
                        'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
                    linkText: 'החזון שלנו',
                    linkHref: '/about',
                },
                {
                    body:
                        'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
                    linkText: 'הערכים שלנו',
                    linkHref: '/about',
                },
            ],
        },

        teamSection: {
            eyebrow: 'הצוות שלנו',
            title: 'Section Title',
            description: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
            members: [
                {
                    slug: 'team-member-one',
                    name: 'Your Business Team',
                    role: 'Item One',
                    description:
                        'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
                    bio: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
                    img: '/placeholder.svg',
                    socials: [],
                    seo: {
                        title: 'צוות {business} | Section Title',
                        description:
                            'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
                        keywords: '{business}, keyword one, keyword two, keyword three',
                        image: '/placeholder.svg',
                    },
                },
            ],
        },

        communitySection: {
            eyebrow: 'העבודה שלנו',
            title: 'Section Title',
            subtitle: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
            images: COMMUNITY_IMAGES,
            ctaText: 'לשירותים שלנו',
            ctaHref: '/services',
        },
    },
};

export function getAboutData(language) {
    return AboutData[language === 'he' ? 'he' : 'en'];
}

export default AboutData;
